import React from 'react';
import { ErrorFallback } from './ErrorFallback';

type ErrorBoundaryProps = {
    children: React.ReactNode;
    'data-testid': string;
    fillSpace?: boolean;
    fallbackMessage: string;
    id: string;
    onRetry?: () => void;
    /**
     * Values that identify the failed attempt. When any of them changes while a
     * caught error is showing, the boundary clears it and re-renders `children`.
     *
     * This is what makes Retry work in one press. `onRetry` starts an async
     * refetch, so at the moment the button is clicked the data behind the child
     * is still the failed data — clearing the error optimistically just lets the
     * child throw again and the fallback returns. Pass the page's error (e.g.
     * `resetKeys={[page.error]}`) and the boundary recovers when the refetch
     * actually resolves, rather than requiring a second click.
     */
    resetKeys?: readonly unknown[];
    retryLabel?: string;
    title: string;
};

type ErrorBoundaryState = {
    error: Error | null;
};

const keysChanged = (
    previous: readonly unknown[] | undefined,
    next: readonly unknown[] | undefined,
): boolean => {
    if (previous === undefined || next === undefined) {
        return false;
    }
    if (previous.length !== next.length) {
        return true;
    }
    return previous.some((value, index) => !Object.is(value, next[index]));
};

export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    public override state: ErrorBoundaryState = {
        error: null,
    };

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    public override componentDidUpdate(previous: ErrorBoundaryProps): void {
        if (
            this.state.error &&
            keysChanged(previous.resetKeys, this.props.resetKeys)
        ) {
            this.setState({ error: null });
        }
    }

    private handleRetry = () => {
        // Clearing here is optimistic: if the underlying data has not changed
        // yet the child throws again and we land back on the fallback. The real
        // recovery path is `resetKeys` above, once the refetch resolves.
        this.setState({ error: null });
        this.props.onRetry?.();
    };

    public override render() {
        if (this.state.error) {
            return (
                <ErrorFallback
                    data-testid={`${this.props.id}-fallback`}
                    error={this.state.error}
                    fillSpace={this.props.fillSpace}
                    id={`${this.props.id}-fallback`}
                    message={this.props.fallbackMessage}
                    onRetry={
                        this.props.onRetry || this.props.retryLabel
                            ? this.handleRetry
                            : undefined
                    }
                    retryLabel={this.props.retryLabel}
                    title={this.props.title}
                />
            );
        }

        return this.props.children;
    }
}
